import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import Icon from './Icon.jsx';
import { ERAS } from '../data/timeline.js';
import { firstEvent, isCorrect, nextEvent, shareRun } from '../lib/beforeAfter.js';
import { copyToClipboard } from '../lib/daily.js';
import { playCorrect, playWrong } from '../lib/sfx.js';

/**
 * Before or After.
 *
 * ── What the screen has to say in one glance ─────────────────────────────────
 *
 * The first version of this got the game right and the *sentence* wrong: it
 * showed two cards and a small grey caption reading "does this come before or
 * after?", and left you to work out which card the question was about. So the
 * whole board is now built around one explicit sentence —
 *
 *     Does this come **before** or **after** *Jacob wrestles at the Jabbok*?
 *
 * — with the anchor named in it, the two words coloured exactly like the two
 * buttons, and an axis strip sitting directly above those buttons so the
 * mapping (left = earlier, right = later) is stated rather than inferred.
 *
 * ── The look ─────────────────────────────────────────────────────────────────
 *
 * Time runs left to right, so BEFORE takes the stained-glass violet and AFTER
 * the gold, and that pair is used nowhere else on the screen. The card in play
 * is therefore *not* gold any more — it's lit parchment, the brightest thing on
 * a dark board, which is what makes it read as the thing being judged.
 *
 * Every card carries its era as a coloured spine down its left edge. Nine eras,
 * nine jewel tones: after a few rounds you start recognising "that violet one
 * is Genesis" before you've read the words, which is the game teaching the
 * shape of the Bible without a lesson.
 *
 * The satisfaction is in the settle. A right answer does not just increment a
 * number — the verdict replaces the question, the date reveals, the card drops
 * into the stack, and only then does the next card rise. Those beats are staged
 * rather than simultaneous, which is the difference between "correct" and
 * *landing* it.
 */

const REVEAL_MS = 900;

/**
 * What a run is worth calling itself.
 *
 * A bare number is a score; a name is something you tell someone. The steps are
 * deliberately reachable — most people will see "Scribe" on their second or
 * third go, and the top one is rare enough to be worth chasing.
 */
const RANKS = [
  { at: 20, name: 'Keeper of the ages' },
  { at: 15, name: 'Sage' },
  { at: 10, name: 'Chronicler' },
  { at: 6, name: 'Scribe' },
  { at: 3, name: 'Reader' },
  { at: 0, name: 'Just opened the book' },
];

const rankFor = (score) => RANKS.find((rank) => score >= rank.at).name;

/**
 * One card. The era drives a CSS custom property rather than a class, so the
 * spine, the era label and the glow all take the same colour from one place.
 *
 * `when` is three-state on purpose: the anchor never shows a date, the card in
 * play keeps it in the DOM but invisible so the card cannot resize at the
 * moment of judgement, and a placed card shows it because that is the payoff.
 */
function Card({ event, variant, when = 'none', tag = null, state = null }) {
  return (
    <div
      className={`ba-card ba-card--${variant}${state ? ` is-${state}` : ''}`}
      data-era={event.era}
    >
      <span className="ba-card__spine" aria-hidden="true" />
      <span className="ba-card__era">{ERAS[event.era]}</span>
      <span className="ba-card__title">{event.title}</span>
      <span className="ba-card__where">{event.where}</span>
      {when === 'none' ? null : (
        <span className={`ba-card__when${when === 'shown' ? ' is-shown' : ''}`}>{event.when}</span>
      )}
      {tag ? <span className="ba-card__tag">{tag}</span> : null}
    </div>
  );
}

/**
 * @param runs      completed runs on this device, including the one just ended
 * @param signedIn  whether there is an account to keep any of this
 * @param onNeedAccount  open the sign-up sheet
 * @param onUpgrade      open the paywall — a quiet second offer, never the first
 */
export default function BeforeAfter({
  best = 0,
  soundOn = true,
  runs = 0,
  signedIn = false,
  onNeedAccount,
  onUpgrade,
  onFinish,
  onExit,
}) {
  const [anchor, setAnchor] = useState(() => firstEvent());
  const [card, setCard] = useState(null);
  const [round, setRound] = useState(0);
  const [score, setScore] = useState(0);
  const [seen, setSeen] = useState(() => new Set());
  const [phase, setPhase] = useState('asking'); // asking | revealing | over
  const [verdict, setVerdict] = useState(null); // 'right' | 'spared' | 'wrong'
  // One mistake a run is forgiven. Losing a fourteen-round chain to a single
  // slip is the thing that makes people put a game down, and the second chance
  // costs nothing except that the run has to be that bit longer to beat.
  const [spare, setSpare] = useState(1);
  const [placed, setPlaced] = useState([]); // the last couple, shown on the board
  const [history, setHistory] = useState([]); // the whole run, shown at the end
  const [copied, setCopied] = useState(false);

  const timers = useRef([]);
  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  // Deal the opening card once the anchor exists.
  useEffect(() => {
    if (card || phase !== 'asking') return;
    const dealt = nextEvent(anchor, 0, new Set([anchor.id]));
    setCard(dealt);
    setSeen(new Set([anchor.id, dealt.id]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answer = useCallback(
    (choice) => {
      if (phase !== 'asking' || !card) return;

      const right = isCorrect(anchor, card, choice);
      const spared = !right && spare > 0;
      const placedAs = card.order < anchor.order ? 'Before' : 'After';
      const entry = { event: card, verdict: right ? 'right' : 'wrong', placedAs };

      setVerdict(right ? 'right' : spared ? 'spared' : 'wrong');
      setPhase('revealing');
      setHistory((list) => [...list, entry]);
      if (soundOn) (right ? playCorrect : playWrong)();

      if (!right && !spared) {
        // Show where it actually belonged before ending the run — the whole
        // point of losing is finding out.
        setPlaced((list) => [...list, entry]);
        timers.current.push(
          setTimeout(() => {
            setPhase('over');
            onFinish?.(score);
          }, REVEAL_MS + 500),
        );
        return;
      }

      if (spared) setSpare(0);
      // A forgiven miss does not score. It still takes its true place on the
      // timeline and the chain carries on from there, which is the only honest
      // way to continue — the card has been shown, so it cannot be asked again.
      if (right) setScore((n) => n + 1);

      // Three kept, two shown: the newest of them is promoted to the anchor a
      // beat later and is filtered out of the stack when it is. Any deeper a
      // pile and it starts competing with the card in play; the whole run gets
      // its own read at the end.
      setPlaced((list) => [...list.slice(-2), entry]);

      timers.current.push(
        setTimeout(() => {
          const nextRound = round + 1;
          const nextSeen = new Set(seen).add(card.id);
          const dealt = nextEvent(card, nextRound, nextSeen);

          if (!dealt) {
            setPhase('over');
            onFinish?.(right ? score + 1 : score);
            return;
          }

          // The card just placed becomes the thing to beat. That is what makes
          // it a chain rather than a series of unrelated pairs.
          setAnchor(card);
          setCard(dealt);
          setSeen(new Set(nextSeen).add(dealt.id));
          setRound(nextRound);
          setVerdict(null);
          setPhase('asking');
        }, spared ? REVEAL_MS + 500 : REVEAL_MS),
      );
    },
    [phase, card, anchor, round, score, seen, spare, soundOn, onFinish],
  );

  // Left and right arrows, for anyone playing at a keyboard. They match the
  // axis strip on screen rather than being an arbitrary binding.
  useEffect(() => {
    const onKey = (event) => {
      if (phase !== 'asking') return;
      if (event.key === 'ArrowLeft') answer('before');
      if (event.key === 'ArrowRight') answer('after');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, answer]);

  const beaten = score > best;
  const shareable = useMemo(() => shareRun(score, best), [score, best]);
  // One complete go, then an account. `runs` already includes the run that has
  // just ended, so this is true on the first game-over screen an anonymous
  // player sees — after they have played, never before.
  const gateReplay = !signedIn && runs >= 1;

  if (phase === 'over') {
    const last = history.at(-1);
    const chain = history.slice(-6);

    return (
      <section className="screen ba">
        <div className="ba-over">
          <p className="ba-over__eyebrow">{beaten ? 'New best' : 'Run over'}</p>
          <p className="ba-over__score">{score}</p>
          <p className="ba-over__label">in a row</p>
          <p className="ba-over__rank">{rankFor(score)}</p>

          {last?.verdict === 'wrong' ? (
            <p className="ba-over__note">
              <strong>{last.event.title}</strong> comes {last.placedAs.toLowerCase()} —{' '}
              {last.event.when.toLowerCase()}.
            </p>
          ) : null}

          {chain.length ? (
            <ol className="ba-over__chain">
              {chain.map(({ event, verdict: v }) => (
                <li key={event.id} className={`ba-over__link is-${v}`}>
                  <span className="ba-over__link-dot" data-era={event.era} aria-hidden="true" />
                  <span className="ba-over__link-title">{event.title}</span>
                  <span className="ba-over__link-when">{event.when}</span>
                </li>
              ))}
            </ol>
          ) : null}

          <div className="ba-over__stats">
            <div>
              <span className="ba-over__stat">{score}</span>
              <span className="ba-over__stat-label">this run</span>
            </div>
            <div>
              <span className="ba-over__stat">{Math.max(best, score)}</span>
              <span className="ba-over__stat-label">best</span>
            </div>
          </div>

          {/*
            The ask, at the only moment it is worth anything: they have just
            seen a score they care about, and it is about to evaporate.

            It replaces Play again rather than sitting beside it. That is the
            whole point of the gate — but the run they just finished was
            complete, and the share button below is still there, because a
            score you cannot show anyone is not worth signing up to keep.
          */}
          {gateReplay ? (
            <div className="ba-gate">
              <p className="ba-gate__lead">
                {beaten ? 'That is your best run.' : `${score} in a row.`} It lives in
                this browser only — clear it and it is gone.
              </p>
              <button
                type="button"
                className="btn btn--primary btn--block btn--lg"
                onClick={onNeedAccount}
              >
                Make a free account to keep playing
              </button>
              <p className="ba-gate__note">
                Free, takes a moment. Keeps your best score and your streak, and
                carries them to your phone.
              </p>
              {/* Quiet, and second. Nobody who arrived a minute ago is buying a
                  subscription, and asking reads as a shop rather than an app. */}
              <button type="button" className="ba-gate__more" onClick={onUpgrade}>
                Or see what Premium opens up
              </button>
            </div>
          ) : (
          <button
            type="button"
            className="btn btn--primary btn--block btn--lg"
            onClick={() => {
              clearTimers();
              const opener = firstEvent();
              const dealt = nextEvent(opener, 0, new Set([opener.id]));
              setAnchor(opener);
              setCard(dealt);
              setSeen(new Set([opener.id, dealt.id]));
              setPlaced([]);
              setHistory([]);
              setScore(0);
              setRound(0);
              setSpare(1);
              setVerdict(null);
              setPhase('asking');
            }}
          >
            Play again
          </button>
          )}

          <button
            type="button"
            className="btn btn--ghost btn--block"
            onClick={async () => {
              if (navigator.share) {
                try {
                  await navigator.share({ text: shareable });
                  return;
                } catch {
                  /* dismissed — fall through to copy */
                }
              }
              if (await copyToClipboard(shareable)) {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }
            }}
          >
            <Icon name="share" size={15} />
            {copied ? 'Copied' : 'Share your run'}
          </button>

          <button type="button" className="btn btn--ghost btn--block" onClick={onExit}>
            Back to practice
          </button>
        </div>
      </section>
    );
  }

  const shown = history.at(-1);
  // Green or red, and nothing else. A forgiven miss is still a miss — dressing
  // it in amber made the moment ambiguous exactly when it needs to be blunt.
  const tone = verdict ? (verdict === 'right' ? 'right' : 'wrong') : null;

  return (
    <section className="screen ba">
      {/* The whole screen takes the colour for half a second. This is the part
          that makes being right feel like something rather than merely read
          like something.

          Portalled to <body> rather than rendered here, and that is not
          tidiness: `.screen` animates in on a transform, and a transformed
          ancestor becomes the containing block for `position: fixed`. Left in
          place the flash was 358×528 — the size of this section — instead of
          the size of the window, which looks like a rendering fault rather
          than a beat. */}
      {tone
        ? createPortal(
            <span key={history.length} className={`ba-flash ba-flash--${tone}`} />,
            document.body,
          )
        : null}
      <header className="ba__bar">
        <button type="button" className="icon-btn" onClick={onExit} aria-label="Leave the game">
          <Icon name="close" size={15} />
        </button>
        <div className="ba__score" aria-live="polite">
          {/* Keyed on the score so React remounts it and the pop animation
              fires on every increment rather than only on mount. */}
          <span className="ba__score-value" key={score}>
            {score}
          </span>
          <span className="ba__score-label">in a row</span>
        </div>
        <div className="ba__right">
          {/* Shown whether it is spent or not: knowing you have a spare changes
              how you play, and knowing you have used it changes it back. */}
          <span className={`ba__spare${spare ? '' : ' is-spent'}`} title="Second chance">
            <Icon name="renew" size={13} />
            {spare ? 'Spare' : 'Spent'}
          </span>
          <span className="ba__best">
            <Icon name="star" size={12} />
            {Math.max(best, score)}
          </span>
        </div>
      </header>

      {/* What you have placed already, newest nearest the anchor. The card
          currently serving as the anchor is filtered out — it is on screen
          twice over already, once as the anchor and once inside the question. */}
      <div className="ba-stack">
        {placed
          .filter(({ event }) => event.id !== anchor.id)
          .map(({ event, verdict: v, placedAs }) => (
            <Card
              key={event.id}
              event={event}
              variant="placed"
              when="shown"
              tag={placedAs}
              state={v}
            />
          ))}
      </div>

      {/* The card to beat, named as such. */}
      <div className="ba-anchor">
        <p className="ba-anchor__label">
          <span>{placed.length ? 'You placed this' : 'Start here'}</span>
        </p>
        <Card event={anchor} variant="anchor" />
      </div>

      {card ? (
        <div className={`ba-play${tone ? ` is-${tone}` : ''}`} key={card.id}>
          <div className="ba-play__head">
            {verdict ? (
              <p className={`ba-verdict ba-verdict--${tone}`} role="status">
                <Icon name={tone === 'right' ? 'check' : 'close'} size={15} />
                <span>
                  <b>{tone === 'right' ? 'Right' : 'Wrong'}</b> — it comes{' '}
                  {shown?.placedAs.toLowerCase()}
                  {verdict === 'spared' ? '. Second chance used.' : null}
                </span>
              </p>
            ) : (
              <p className="ba-play__ask">
                Does this come <b className="ba-word ba-word--before">before</b> or{' '}
                <b className="ba-word ba-word--after">after</b>{' '}
                <em className="ba-play__anchor">{anchor.title}</em>?
              </p>
            )}
          </div>

          <Card event={card} variant="play" when={verdict ? 'shown' : 'hidden'} />
        </div>
      ) : null}

      <div className="ba-choose">
        {/* The axis is stated, not inferred. It sits directly over the buttons
            so each end label reads as a caption for the button beneath it. */}
        <div className="ba-choose__axis" aria-hidden="true">
          <span className="ba-choose__end ba-choose__end--before">Earlier</span>
          <span className="ba-choose__line" />
          <span className="ba-choose__end ba-choose__end--after">Later</span>
        </div>

        <div className="ba-choose__row">
          <button
            type="button"
            className="ba-btn ba-btn--before"
            onClick={() => answer('before')}
            disabled={phase !== 'asking'}
          >
            <Icon name="arrow" size={20} className="ba-btn__arrow ba-btn__arrow--left" />
            <span className="ba-btn__word">Before</span>
            <span className="ba-btn__hint">earlier in the story</span>
          </button>
          <button
            type="button"
            className="ba-btn ba-btn--after"
            onClick={() => answer('after')}
            disabled={phase !== 'asking'}
          >
            <span className="ba-btn__word">After</span>
            <Icon name="arrow" size={20} className="ba-btn__arrow" />
            <span className="ba-btn__hint">later in the story</span>
          </button>
        </div>
      </div>
    </section>
  );
}
